import { createLoop, fitCanvas, mulberry32, pointerTracker } from "./shared.js";

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
  return shader;
}

function mountCanvasFallback(canvas, settings, pointer) {
  const random = mulberry32(settings.seed + 91);
  const particles = Array.from({ length: Math.round(680 * settings.density) }, () => ({
    x: random(),
    y: random(),
    phase: random() * Math.PI * 2,
    radius: random(),
  }));
  return createLoop((_, time) => {
    const { ctx, width, height } = fitCanvas(canvas, settings.quality);
    ctx.fillStyle = settings.trails ? "rgba(8,13,21,.18)" : "#080d15";
    ctx.fillRect(0, 0, width, height);
    const px = pointer.active ? pointer.x : 0.5 + Math.sin(time * 0.4) * 0.18;
    const py = pointer.active ? pointer.y : 0.5 + Math.cos(time * 0.32) * 0.18;
    particles.forEach((particle, index) => {
      const angle = particle.phase + time * (0.2 + particle.radius * 0.3);
      const orbit = 0.04 + particle.radius * 0.42;
      const x = (px + Math.cos(angle * 1.3) * orbit + index * 0.00017) % 1;
      const y = (py + Math.sin(angle) * orbit * 0.7 + 1) % 1;
      ctx.fillStyle = index % 9 === 0 ? "#d54a36" : "#e8d9b8";
      ctx.globalAlpha = 0.22 + particle.radius * 0.78;
      ctx.fillRect(x * width, y * height, 1.2 + particle.radius * 2, 1.2 + particle.radius * 2);
    });
    ctx.globalAlpha = 1;
  }, settings);
}

export function mountSparks(root, settings) {
  const canvas = document.createElement("canvas");
  canvas.className = "scene-canvas sparks-canvas";
  root.append(canvas);
  const pointer = pointerTracker(canvas);
  const gl = canvas.getContext("webgl", {
    alpha: false,
    antialias: false,
    preserveDrawingBuffer: false,
  });
  if (!gl || settings.quality === "eco") {
    root.dataset.renderer = "Canvas 2D fallback";
    return mountCanvasFallback(canvas, settings, pointer);
  }

  root.dataset.renderer = "Native WebGL";
  const vertex = compile(
    gl,
    gl.VERTEX_SHADER,
    `
      attribute vec3 a_seed;
      uniform float u_time;
      uniform vec2 u_pointer;
      uniform float u_density;
      varying float v_glow;
      void main() {
        float angle = a_seed.x * 6.28318 + u_time * (.11 + a_seed.z * .18);
        float radius = .06 + a_seed.y * .82;
        vec2 orbit = vec2(cos(angle * 1.7), sin(angle)) * radius;
        orbit.y *= .72;
        vec2 constellation = vec2(
          sin(a_seed.x * 31.0 + u_time * .21),
          cos(a_seed.y * 23.0 - u_time * .17)
        ) * .07;
        vec2 pos = (u_pointer * 2.0 - 1.0) + orbit + constellation;
        pos *= .82;
        gl_Position = vec4(pos.x, -pos.y, 0.0, 1.0);
        gl_PointSize = (1.4 + a_seed.z * 4.2) * u_density;
        v_glow = a_seed.z;
      }`,
  );
  const fragment = compile(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision mediump float;
      varying float v_glow;
      void main() {
        vec2 p = gl_PointCoord - .5;
        float d = length(p);
        float alpha = smoothstep(.5, .05, d);
        vec3 paper = vec3(.92, .83, .65);
        vec3 red = vec3(.86, .20, .12);
        vec3 color = mix(paper, red, step(.94, v_glow));
        gl_FragColor = vec4(color, alpha * (.32 + v_glow * .68));
      }`,
  );
  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
  gl.useProgram(program);
  const count = Math.round(2200 * settings.density);
  const random = mulberry32(settings.seed + 91);
  const data = new Float32Array(count * 3);
  for (let i = 0; i < data.length; i += 1) data[i] = random();
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  const seedLocation = gl.getAttribLocation(program, "a_seed");
  gl.enableVertexAttribArray(seedLocation);
  gl.vertexAttribPointer(seedLocation, 3, gl.FLOAT, false, 0, 0);
  const timeLocation = gl.getUniformLocation(program, "u_time");
  const pointerLocation = gl.getUniformLocation(program, "u_pointer");
  const densityLocation = gl.getUniformLocation(program, "u_density");
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

  return createLoop((_, time) => {
    const rect = canvas.getBoundingClientRect();
    const dpr = settings.quality === "high" ? Math.min(2, devicePixelRatio) : 1;
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    }
    if (!settings.trails) gl.clearColor(0.03, 0.05, 0.08, 1);
    else gl.clearColor(0.03, 0.05, 0.08, 0.17);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const px = pointer.active ? pointer.x : 0.5 + Math.sin(time * 0.4) * 0.18;
    const py = pointer.active ? pointer.y : 0.5 + Math.cos(time * 0.32) * 0.18;
    gl.uniform1f(timeLocation, time * settings.speed);
    gl.uniform2f(pointerLocation, px, py);
    gl.uniform1f(densityLocation, dpr);
    gl.drawArrays(gl.POINTS, 0, count);
  }, settings);
}
